// ===============================
// OngletListeMedecinsGardes.jsx
// ===============================
// Page responsable : Liste des médecins pour les gardes
// Permet de sélectionner quels médecins de la liste maître sont concernés par les gardes
// ===============================

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext.jsx";
import { apiFetch, fetchMedecinsMaitre, readApiError } from "./apiMedecins.js";
import { confirmDialog, notify } from "./ui/dialogService.js";
import { collapseResponsibleSelfBack } from "./responsableBack.js";
import { goContextualBack } from "./contextualBack.js";

function clampQuotitePct(n) {
  const v = Number.isFinite(n) ? n : Number(n);
  if (!Number.isFinite(v)) return 100;
  return Math.min(100, Math.max(1, Math.round(v)));
}

function normalizeMedecin(m) {
  const safe = m || {};
  const serviceQuotite = clampQuotitePct(
    Number.isFinite(safe.quotite) ? safe.quotite : Number(safe.quotite) || 100
  );
  const prRaw = safe.quotite_port_royal;
  const quotite_port_royal = Number.isFinite(Number(prRaw))
    ? clampQuotitePct(prRaw)
    : serviceQuotite;

  return {
    nom: typeof safe.nom === "string" ? safe.nom : "",
    prenom: typeof safe.prenom === "string" ? safe.prenom : "",
    statut: typeof safe.statut === "string" && safe.statut.trim() ? safe.statut : "Praticien hospitalier",
    quotite: serviceQuotite,
    quotite_port_royal,
    medecin_id: typeof safe.medecin_id === "string" ? safe.medecin_id : "",
    categorie_junior: typeof safe.categorie_junior === "string" ? safe.categorie_junior : "senior",
    // ✅ Champ spécifique astreinte thoracique : interieur / exterieur
    origine: typeof safe.origine === "string" && safe.origine.trim() ? safe.origine : "interieur",
  };
}

/** Liste PR1 : quotité service = liste maître ; quotité PR = champ dédié (n’écrit pas la base médecins). */
function buildPr1PayloadForSave(gardesList, masterList) {
  return gardesList.map((g) => {
    const master = masterList.find((x) => isSameMedecin(x, g));
    const serviceQ = master ? clampQuotitePct(master.quotite) : clampQuotitePct(g.quotite);
    const prQ = clampQuotitePct(g.quotite_port_royal ?? serviceQ);
    return {
      nom: g.nom,
      prenom: g.prenom,
      statut: g.statut || "Praticien hospitalier",
      quotite: serviceQ,
      quotite_port_royal: prQ,
      medecin_id: g.medecin_id || "",
      origine: g.origine === "exterieur" ? "exterieur" : "interieur",
    };
  });
}

function sortMedecins(list) {
  return [...list].sort((a, b) => {
    const an = String(a.nom || "");
    const bn = String(b.nom || "");
    const cmp = an.localeCompare(bn, "fr", { sensitivity: "base" });
    if (cmp !== 0) return cmp;
    return String(a.prenom || "").localeCompare(String(b.prenom || ""), "fr", { sensitivity: "base" });
  });
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

const CATEGORIE_JUNIOR_LABELS = {
  interne_junior_pur: "Interne junior pur",
  dr_junior: "Docteur junior",
  stagiaire_associe: "Stagiaire associé",
};

function normalizeCategorieJunior(value) {
  const normalized = String(value || "senior").trim().toLowerCase();
  return normalized && normalized !== "senior" ? normalized : "senior";
}

function categorieJuniorLabel(value) {
  return CATEGORIE_JUNIOR_LABELS[normalizeCategorieJunior(value)] || "Junior";
}

function medecinKey(medecin) {
  const medecinId = String(medecin?.medecin_id || "").trim();
  if (medecinId) return `id:${medecinId}`;
  const nom = normalizeText(medecin?.nom);
  const prenom = normalizeText(medecin?.prenom);
  return `np:${nom}|${prenom}`;
}

function normalizeResponsibleBackPath(path) {
  const raw = String(path || "").trim();
  if (!raw) return "";
  try {
    const isAbsolute = /^[a-z][a-z\d+\-.]*:\/\//i.test(raw);
    const baseOrigin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://planning.local";
    const url = new URL(raw, baseOrigin);
    if (url.pathname === "/responsable/index.html") {
      url.pathname = "/responsable";
    }
    if (isAbsolute) return url.href;
    return collapseResponsibleSelfBack(`${url.pathname}${url.search || ""}${url.hash || ""}`);
  } catch {
    // Conserver le chemin initial si ce n'est pas une URL parseable.
  }
  return raw;
}

function isAllowedResponsibleBackPath(path, isSupervisionUser) {
  const raw = String(path || "").trim();
  if (!raw) return false;
  if (!isSupervisionUser && raw.includes("/superviseur")) return false;
  if (raw.startsWith("/")) return true;
  try {
    if (typeof window === "undefined" || !window.location) return false;
    const url = new URL(raw);
    if (url.origin === window.location.origin) return true;
    const currentHost = window.location.hostname;
    const localHost = currentHost === "127.0.0.1" || currentHost === "localhost";
    const targetHost = url.hostname === "127.0.0.1" || url.hostname === "localhost";
    return localHost && targetHost && url.port === "4101";
  } catch {
    return false;
  }
}

function supervisorBackPathFrom(path) {
  const raw = String(path || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw, typeof window !== "undefined" ? window.location.origin : "http://planning.local");
    if (url.pathname.includes("/superviseur")) {
      return `${url.pathname}${url.search || ""}${url.hash || ""}`;
    }
    const nestedBack = url.searchParams.get("back") || "";
    if (!nestedBack) return "";
    const nestedUrl = new URL(nestedBack, url.origin);
    if (!nestedUrl.pathname.includes("/superviseur")) return "";
    return `${nestedUrl.pathname}${nestedUrl.search || ""}${nestedUrl.hash || ""}`;
  } catch {
    return raw.includes("/superviseur") ? raw : "";
  }
}

function isSameMedecin(a, b) {
  return medecinKey(a) === medecinKey(b);
}

/** Saisie libre (effacer puis taper), sans flèches type number ; commit au blur ou Entrée. */
function PrQuotiteInput({ value, onCommit, style }) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  const commit = () => {
    const trimmed = String(text).trim();
    if (trimmed === "") {
      setText(String(value));
      return;
    }
    const n = parseInt(trimmed, 10);
    const clamped = clampQuotitePct(Number.isFinite(n) ? n : value);
    onCommit(clamped);
    setText(String(clamped));
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      aria-label="Quotité Port-Royal en pourcentage"
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 3);
        setText(digits);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      style={style}
    />
  );
}

// Fonction pour vérifier si un médecin est dans la liste Gardes
function isInList(medecin, list) {
  return list.some((m) => isSameMedecin(m, medecin));
}

function NativeToggleCheckbox({ checked, onToggle, style, ariaLabel }) {
  const inputRef = useRef(null);
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;

    let timeoutId;
    const handleClick = (event) => {
      event.preventDefault();
      timeoutId = window.setTimeout(() => onToggleRef.current(), 0);
    };

    input.addEventListener("click", handleClick);
    return () => {
      input.removeEventListener("click", handleClick);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <input
      ref={inputRef}
      type="checkbox"
      checked={checked}
      readOnly
      aria-label={ariaLabel}
      style={style}
    />
  );
}

export default function OngletListeMedecinsGardes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locationSearchRaw = typeof window !== "undefined" ? String(window.location.search || "") : "";
  const rawTypeParam = searchParams.get("type") || new URLSearchParams(locationSearchRaw).get("type") || "";
  const rawBackParam = searchParams.get("back") || new URLSearchParams(locationSearchRaw).get("back") || "";
  const decodedBackParam = String(rawBackParam || "").trim();
  const normalizedType = decodeURIComponent(String(rawTypeParam))
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

  const isAstreinteThoracique = normalizedType === "ASTREINTE_THORACIQUE";
  const isDayPlanningRoster = normalizedType === "UCA" || normalizedType === "OPH";
  const isPR1 = normalizedType === "PR1";
  const isOrtho = normalizedType === "ORTHOPEDIE";
  const isOrthoJunior = normalizedType === "ORTHOPEDIE_JUNIOR";
  const isInterne1 = normalizedType === "INTERNE1";
  const isInterne2 = normalizedType === "INTERNE2";
  const isDigestif = normalizedType === "DIGESTIF";
  const isReanimation =
    normalizedType === "REANIMATION" ||
    locationSearchRaw.toUpperCase().includes("TYPE=REANIMATION");
  const isDouleur =
    normalizedType === "DOULEUR" || locationSearchRaw.toUpperCase().includes("TYPE=DOULEUR");
  const isInterneDigestif = normalizedType === "INTERNE_DIGESTIF";
  const isInterneRea = normalizedType === "INTERNE_REA";
  const isJuniorDutyList = isInterne1 || isInterne2 || isInterneDigestif || isInterneRea;
  const libelleSecteur = isDayPlanningRoster ? `Secteur ${normalizedType}` : isAstreinteThoracique
    ? "Secteur thoracique"
    : isOrtho
    ? "Astreinte Orthopédie"
    : isOrthoJunior
    ? "Astreinte junior Orthopédie"
    : isPR1
    ? "secteur Port-Royal"
    : isInterne1
    ? "Gardes Interne PR1"
    : isInterne2
    ? "Gardes Interne PR2"
    : isDigestif
    ? "Secteur digestif"
    : isDouleur
    ? "Gardes Douleur"
    : isReanimation
    ? "Gardes Réanimation"
    : isInterneDigestif
    ? "Gardes Interne Digestif"
    : isInterneRea
    ? "Gardes Interne Réa"
    : "Gardes PR2";

  const [medecinsMaître, setMedecinsMaître] = useState([]); // Liste complète du service
  const [medecinsGardes, setMedecinsGardes] = useState([]); // Liste Gardes actuelle
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("senior");
  const [searchTerm, setSearchTerm] = useState("");

  // Endpoint selon secteur : PR2, PR1 et Astreinte thoracique ont chacun leur liste
  const apiListeSecteur = isDayPlanningRoster
    ? `/api/v2/planning-${normalizedType.toLowerCase()}/medecins`
    : isAstreinteThoracique
    ? "/api/v2/medecins-astreintes-thoracique"
    : isOrtho
    ? "/api/v2/medecins-astreintes-orthopedie"
    : isOrthoJunior
    ? "/api/v2/medecins-astreintes-orthopedie-junior"
    : isPR1
    ? "/api/v2/medecins-gardes-pr1"
    : isInterne1
    ? "/api/v2/medecins-gardes-interne1"
    : isInterne2
    ? "/api/v2/medecins-gardes-interne2"
    : isDigestif
    ? "/api/v2/medecins-gardes-digestif"
    : isDouleur
    ? "/api/v2/medecins-gardes-douleur"
    : isReanimation
    ? "/api/v2/medecins-gardes-reanimation"
    : isInterneDigestif
    ? "/api/v2/medecins-gardes-interne-digestif"
    : isInterneRea
    ? "/api/v2/medecins-gardes-interne-rea"
    : "/api/v2/medecins-gardes-pr2";

  // Chargement des listes (liste maître + liste du secteur)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        // Charger en parallèle la liste maître et la liste du secteur
        // pour réduire le temps d'affichage initial.
        const [dataMaître, resSecteur] = await Promise.all([
          isOrthoJunior
            ? apiFetch(`${apiListeSecteur}?catalogue=1`, { cache: "no-store" }).then(async res => {
              if (!res.ok) throw new Error("Catalogue junior indisponible");
              return res.json();
            })
            : fetchMedecinsMaitre(),
          apiFetch(apiListeSecteur, { cache: "no-store" }),
        ]);
        if (isOrthoJunior && !resSecteur.ok) throw new Error("Participants indisponibles");
        const dataSecteur = resSecteur.ok ? (await resSecteur.json()) || [] : [];
        const normalizedMaster = dataMaître.map(normalizeMedecin);
        const filteredMaster = isOrthoJunior || isJuniorDutyList
          ? normalizedMaster.filter((m) => String(m.categorie_junior || "senior") !== "senior")
          : normalizedMaster;
        setMedecinsMaître(sortMedecins(filteredMaster));
        setMedecinsGardes(sortMedecins(dataSecteur.map(normalizeMedecin)));
      } catch (err) {
        if (isOrthoJunior) setMedecinsMaître([]);
        console.error("❌ Erreur chargement listes médecins :", err);
        notify("Erreur lors du chargement des listes de médecins.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiListeSecteur, isJuniorDutyList, isOrthoJunior]);

  // Toggle : ajouter/retirer un médecin de la liste Gardes
  const toggleMedecin = (medecin) => {
    setMedecinsGardes((prev) => {
      const isIn = prev.some((m) => isSameMedecin(m, medecin));
      if (isIn) {
        // Retirer
        return sortMedecins(prev.filter((m) => !isSameMedecin(m, medecin)));
      }
      // Ajouter (copie depuis la liste maître avec toutes les infos)
      const toAdd = medecinsMaître.find((m) => isSameMedecin(m, medecin)) || medecin;
      return sortMedecins([...prev, normalizeMedecin(toAdd)]);
    });
  };

  const changeOrigine = (medecin, newOrigine) => {
    const origine = newOrigine === "exterieur" ? "exterieur" : "interieur";
    setMedecinsGardes((prev) => {
      const isIn = prev.some((m) => isSameMedecin(m, medecin));
      if (isIn) {
        return sortMedecins(
          prev.map((g) => (isSameMedecin(g, medecin) ? { ...g, origine } : g))
        );
      }
      const toAdd = medecinsMaître.find((m) => isSameMedecin(m, medecin)) || medecin;
      return sortMedecins([...prev, { ...normalizeMedecin(toAdd), origine }]);
    });
  };

  // Sauvegarde de la liste du secteur (Gardes PR2 ou Astreinte thoracique)
  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = isPR1 ? buildPr1PayloadForSave(medecinsGardes, medecinsMaître) : medecinsGardes;
      if (isOrthoJunior && medecinsMaître.length === 0) throw new Error("Le catalogue doit être chargé avant d’enregistrer.");
      const res = await apiFetch(isOrthoJunior && payload.length === 0 ? `${apiListeSecteur}?confirmClear=true` : apiListeSecteur, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(await readApiError(res, `HTTP ${res.status}: erreur sauvegarde liste`));
      }

      const result = await res.json().catch(() => ({ ok: true }));
      const savedList = Array.isArray(result?.list)
        ? sortMedecins(result.list.map(normalizeMedecin))
        : sortMedecins(payload.map(normalizeMedecin));
      setMedecinsGardes(savedList);

      const countAffiché = medecinsMaître.filter((m) => isInList(m, savedList)).length;
      const unresolved = Array.isArray(result?.unresolved) ? result.unresolved : [];
      const unresolvedMessage = unresolved.length
        ? `\n\nAttention : ${unresolved.length} médecin(s) non résolu(s) par l'API n'ont pas été enregistrés.`
        : "";
      notify(
        isPR1
          ? `✅ Liste des médecins secteur Port-Royal sauvegardée (${countAffiché} médecins).${unresolvedMessage}`
          : `✅ Liste des médecins – ${libelleSecteur} sauvegardée (${countAffiché} médecins).${unresolvedMessage}`
      );
    } catch (err) {
      console.error("❌ Erreur sauvegarde liste Gardes :", err);
      notify(err.message || "Erreur lors de la sauvegarde de la liste Gardes.");
    } finally {
      setSaving(false);
    }
  };

  // Réinitialiser depuis la liste maître
  const handleResetFromMaître = async () => {
    if (
      !(await confirmDialog(
        isPR1
          ? "Réinitialiser la liste du secteur Port-Royal avec tous les médecins du service ?"
          : `Réinitialiser la liste ${libelleSecteur} avec tous les médecins du service ?`
      ))
    )
      return;
    setMedecinsGardes(sortMedecins(medecinsMaître.map(normalizeMedecin)));
  };

  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "24px auto",
      padding: "24px",
      background: "#fff",
      borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      fontFamily: "Inter, sans-serif",
    },
    title: { fontSize: "28px", fontWeight: 700, marginBottom: "8px" },
    subtitle: { fontSize: "16px", color: "#6b7280", marginBottom: "20px" },
    infoBox: {
      background: "#f3f4f6",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      padding: "16px",
      marginBottom: "20px",
    },
    table: { width: "100%", borderCollapse: "collapse", marginTop: "16px" },
    th: {
      textAlign: "left",
      borderBottom: "2px solid #e5e7eb",
      padding: "10px",
      background: "#f9fafb",
      fontWeight: 600,
    },
    td: {
      borderBottom: "1px solid #f1f5f9",
      padding: "10px",
      fontSize: "16px",
    },
    checkbox: {
      width: "20px",
      height: "20px",
      cursor: "pointer",
    },
    button: {
      padding: "10px 16px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: "16px",
      marginRight: "12px",
      marginTop: "20px",
    },
    btnPrimary: { background: "#111827", color: "#fff" },
    btnSecondary: { background: "#374151", color: "#fff" },
    btnDanger: { background: "#dc2626", color: "#fff" },
    btnDisabled: { background: "#9ca3af", color: "#fff", cursor: "not-allowed" },
    badge: {
      display: "inline-block",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: 600,
      marginLeft: "8px",
    },
    badgeIn: { background: "#d1fae5", color: "#065f46" },
    badgeOut: { background: "#fee2e2", color: "#991b1b" },
    tabs: {
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
      marginBottom: "16px",
      borderBottom: "1px solid #e5e7eb",
      paddingBottom: "8px",
    },
    tab: {
      padding: "9px 14px",
      borderRadius: "8px",
      border: "1px solid #d1d5db",
      cursor: "pointer",
      fontWeight: 700,
      background: "#fff",
      color: "#374151",
    },
    tabActive: { background: "#111827", color: "#fff", border: "1px solid #111827" },
    categoryBadge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 8px",
      borderRadius: "999px",
      background: "#eef2ff",
      color: "#3730a3",
      fontSize: "13px",
      fontWeight: 700,
      whiteSpace: "nowrap",
    },
    search: {
      width: "100%",
      maxWidth: "420px",
      boxSizing: "border-box",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "16px",
      marginBottom: "16px",
    },
  };

  const selectedBadgeLabel = isAstreinteThoracique
    ? "Thoracique"
    : isOrtho
    ? "Ortho"
    : isOrthoJunior
    ? "Ortho junior"
    : isInterne1
    ? "Interne1"
    : isInterne2
    ? "Interne2"
    : isDigestif
    ? "Digestif"
    : isDouleur
    ? "Douleur"
    : isReanimation
    ? "Réanimation"
    : isInterneDigestif
    ? "Interne Digestif"
    : isInterneRea
    ? "Interne Réa"
    : isPR1
    ? "PR1"
    : "PR2";

  const unselectedBadgeLabel = `Non ${selectedBadgeLabel}`;
  const showOrigineColumn = true;
  const showStatusBadge = !isReanimation && !isDouleur && !isDayPlanningRoster;
  const usesCategoryTabs =
    !isInterne1 && !isInterne2 && !isInterneDigestif && !isInterneRea && !isOrthoJunior;
  const seniorsCount = medecinsMaître.filter(
    (m) => normalizeCategorieJunior(m.categorie_junior) === "senior"
  ).length;
  const juniorsCount = medecinsMaître.length - seniorsCount;
  const normalizedSearchTerm = normalizeText(searchTerm);
  const displayedMedecins = medecinsMaître.filter((m) => {
    const isSenior = normalizeCategorieJunior(m.categorie_junior) === "senior";
    if (usesCategoryTabs && (activeTab === "senior" ? !isSenior : isSenior)) return false;
    if (!normalizedSearchTerm) return true;
    return normalizeText(`${m.nom} ${m.prenom} ${m.statut}`).includes(normalizedSearchTerm);
  });
  const roleNorm = String(user?.role || "").trim().toLowerCase();
  const isSupervisionUser = roleNorm === "supervision";
  const isForbiddenBackPath = (p) => String(p || "").includes("/superviseur");
  const safeDecodedBackParam =
    decodedBackParam && !(!isSupervisionUser && isForbiddenBackPath(decodedBackParam))
      ? decodedBackParam
      : "";
  const normalizedBackParam = normalizeResponsibleBackPath(safeDecodedBackParam);
  const hasBackParam = isAllowedResponsibleBackPath(normalizedBackParam, isSupervisionUser);
  const referrerBackPath = (() => {
    if (typeof window === "undefined" || !window.document?.referrer) return "";
    try {
      const ref = new URL(window.document.referrer);
      const sameOrigin = ref.origin === window.location.origin;
      if (!sameOrigin) return "";
      // Accepter toute page précédente interne (notamment gestion planning multisecteur),
      // sauf si le referrer pointe déjà sur cette page pour éviter les boucles.
      if (ref.pathname.includes("/responsable/gardes-liste-medecins")) return "";
      const candidate = `${ref.pathname}${ref.search || ""}`;
      if (!isSupervisionUser && isForbiddenBackPath(candidate)) return "";
      return candidate;
    } catch {
      return "";
    }
  })();
  const normalizedReferrerBackPath = normalizeResponsibleBackPath(referrerBackPath);
  const fallbackResponsibleBackPath = isDayPlanningRoster
    ? `/multisecteur/responsable/index.html?secteur=sect-${normalizedType.toLowerCase()}&lockSecteur=1`
    : isAstreinteThoracique
    ? "/responsable?secteur=sect-thoracique"
    : isDigestif || isInterneDigestif
    ? "/responsable?secteur=sect-digestif"
    : isDouleur
    ? "/responsable?secteur=sect-douleur"
    : isReanimation || isInterneRea
    ? "/responsable?secteur=sect-reanimation"
    : isOrtho || isOrthoJunior
    ? "/responsable?secteur=sect-orthopedie"
    : "/responsable?secteur=sect-port-royal";
  const supervisionBackPath = isSupervisionUser
    ? supervisorBackPathFrom(normalizedBackParam) || supervisorBackPathFrom(normalizedReferrerBackPath)
    : "";
  const resolvedBackPath = hasBackParam
    ? normalizedBackParam || supervisionBackPath
    : supervisionBackPath || normalizedReferrerBackPath || (isSupervisionUser ? "/superviseur/index.html" : fallbackResponsibleBackPath);
  const hasBrowserBack = typeof window !== "undefined" && window.history.length > 1;
  const canUseSimpleBack = Boolean(resolvedBackPath) || hasBrowserBack;
  const handleBack = () => {
    if (resolvedBackPath) {
      goContextualBack(navigate, fallbackResponsibleBackPath);
      return;
    }
    if (hasBrowserBack) {
      window.history.back();
      return;
    }
    navigate(
      isOrtho
        ? "/responsable/gardes?type=ORTHOPEDIE"
        : isOrthoJunior
        ? "/responsable/gardes?type=ORTHOPEDIE_JUNIOR"
        : isPR1
        ? "/responsable/gardes?type=PR1"
        : isInterne1
        ? "/responsable/gardes?type=INTERNE1"
        : isInterne2
        ? "/responsable/gardes?type=INTERNE2"
        : isDigestif
        ? "/responsable/gardes?type=DIGESTIF"
        : isDouleur
        ? "/responsable/gardes?type=DOULEUR"
        : isReanimation
        ? "/responsable/gardes?type=REANIMATION"
        : "/responsable/gardes?type=PR2"
    );
  };

  return (
    <div style={styles.container}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
        <div>
          <h1 style={styles.title}>
            {isDayPlanningRoster ? `Liste des médecins – ${libelleSecteur}` : isAstreinteThoracique
              ? "🫁 Thoracique, liste des médecins du secteur thoracique"
              : isDigestif
              ? "🩺 Secteur digestif, liste des médecins du secteur digestif"
              : isDouleur
              ? "💊 Secteur douleur, liste des médecins du secteur douleur"
              : isOrtho
              ? "🦴 Liste des médecins – Astreinte Orthopédie"
              : isOrthoJunior
              ? "🦴 Liste des médecins – Astreinte junior Orthopédie"
              : isInterne1
              ? "📅 Liste de garde Interne Port Royal 1"
              : isInterne2
              ? "📅 Liste de garde Interne Port Royal 2"
              : isPR1
              ? "📅 Liste des médecins secteur Port-Royal"
              : `📅 Liste des médecins – ${libelleSecteur}`}
          </h1>
          {!isDayPlanningRoster && <p style={styles.subtitle}>
            {isAstreinteThoracique
              ? "Sélectionnez les médecins participants au secteur thoracique."
              : isDigestif
              ? "Sélectionnez les médecins participants au secteur digestif."
              : isDouleur
              ? "Sélectionnez les médecins participants au secteur douleur."
              : isOrtho
              ? "Sélectionnez les médecins participants à l'astreinte orthopédie."
              : isOrthoJunior
              ? "Sélectionnez les médecins juniors participants à l'astreinte junior orthopédie."
              : isInterne1
              ? "Sélectionnez les médecins concernés par les gardes Interne Port Royal 1."
              : isInterne2
              ? "Sélectionnez les médecins concernés par les gardes Interne Port Royal 2."
              : isPR1
              ? "Sélectionnez les médecins concernés par le secteur Port-Royal (gardes PR1, intégration planning, etc.)."
              : "Sélectionnez les médecins concernés par les gardes."}
            <br />
            {isAstreinteThoracique
              ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas du secteur thoracique."
              : isDigestif
              ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas du secteur digestif."
              : isDouleur
              ? "Seuls les médecins sélectionnés apparaîtront dans les listes et désidératas du secteur douleur."
              : isOrtho
              ? "Seuls les médecins sélectionnés apparaîtront dans les astreintes orthopédie."
              : isOrthoJunior
              ? "Seuls les médecins juniors sélectionnés apparaîtront dans l’astreinte junior Orthopédie."
              : isInterne1
              ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas de garde Interne Port Royal 1."
              : isInterne2
              ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas de garde Interne Port Royal 2."
              : isPR1
              ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas de garde et listes associées au secteur Port-Royal."
              : "Seuls les médecins sélectionnés apparaîtront dans les désidératas de garde."}
            {isPR1 && (
              <>
                <br />
                <strong>Quotité Port-Royal</strong> : modifiable uniquement pour les médecins cochés ; elle ne change pas la quotité globale du service (liste maître / base médecins).
              </>
            )}
          </p>}
        </div>
      </div>
      {!isDayPlanningRoster && <p style={styles.subtitle}>
        {isAstreinteThoracique
          ? "Sélectionnez les médecins participants au secteur thoracique."
          : isDigestif
          ? "Sélectionnez les médecins participants au secteur digestif."
          : isDouleur
          ? "Sélectionnez les médecins participants au secteur douleur."
          : isOrtho
          ? "Sélectionnez les médecins participants à l'astreinte orthopédie."
          : isOrthoJunior
          ? "Sélectionnez les médecins juniors participants à l'astreinte junior orthopédie."
          : isInterne1
          ? "Sélectionnez les médecins concernés par les gardes Interne Port Royal 1."
          : isInterne2
          ? "Sélectionnez les médecins concernés par les gardes Interne Port Royal 2."
          : isPR1
          ? "Sélectionnez les médecins concernés par le secteur Port-Royal (gardes PR1, intégration planning, etc.)."
          : "Sélectionnez les médecins concernés par les gardes."}
        <br />
        {isAstreinteThoracique
          ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas du secteur thoracique."
          : isDigestif
          ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas du secteur digestif."
          : isDouleur
          ? "Seuls les médecins sélectionnés apparaîtront dans les listes et désidératas du secteur douleur."
          : isOrtho
          ? "Seuls les médecins sélectionnés apparaîtront dans les astreintes orthopédie."
          : isOrthoJunior
          ? "Seuls les médecins juniors sélectionnés apparaîtront dans l’astreinte junior Orthopédie."
          : isInterne1
          ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas de garde Interne Port Royal 1."
          : isInterne2
          ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas de garde Interne Port Royal 2."
          : isPR1
          ? "Seuls les médecins sélectionnés apparaîtront dans les désidératas de garde et listes associées au secteur Port-Royal."
          : "Seuls les médecins sélectionnés apparaîtront dans les désidératas de garde."}
        {isPR1 && (
          <>
            <br />
            Colonnes <strong>Quotité service</strong> (liste maître) et <strong>Quotité Port-Royal</strong> (temps sur ce secteur, ex. 50 % pour un temps plein ailleurs).
          </>
        )}
      </p>}

      <div style={styles.infoBox}>
        <div style={{ fontWeight: 600, marginBottom: "8px" }}>📊 Statistiques</div>
        <div>
          <strong>Médecins dans la liste maître :</strong> {medecinsMaître.length}
        </div>
        <div>
          <strong>Médecins sélectionnés {isAstreinteThoracique ? "pour le secteur thoracique" : isDigestif ? "pour le secteur digestif" : isDouleur ? "pour le secteur douleur" : isPR1 ? "pour le secteur Port-Royal" : `pour les ${libelleSecteur.toLowerCase()}`} :</strong>{" "}
          {medecinsMaître.filter((m) => isInList(m, medecinsGardes)).length}
        </div>
        <div style={{ marginTop: "8px", fontSize: "14px", color: "#6b7280" }}>
          💡 Cochez/décochez les médecins pour les ajouter/retirer{" "}
          {isPR1 ? "de la liste du secteur Port-Royal." : `de la liste ${libelleSecteur}.`}
          {isPR1 && " Ajustez la colonne « Quotité Port-Royal » pour les temps partiels sur le secteur uniquement."}
        </div>
      </div>

      {loading ? (
        <p>Chargement…</p>
      ) : (
        <>
          {usesCategoryTabs && (
            <div style={styles.tabs} role="tablist" aria-label="Catégorie médecins">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "senior"}
                style={{ ...styles.tab, ...(activeTab === "senior" ? styles.tabActive : {}) }}
                onClick={() => setActiveTab("senior")}
              >
                Seniors ({seniorsCount})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "junior"}
                style={{ ...styles.tab, ...(activeTab === "junior" ? styles.tabActive : {}) }}
                onClick={() => setActiveTab("junior")}
              >
                Juniors ({juniorsCount})
              </button>
            </div>
          )}

          <input
            type="search"
            aria-label="Rechercher un médecin"
            placeholder="Rechercher par nom, prénom ou statut"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            style={styles.search}
          />

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Sélectionné</th>
                <th style={styles.th}>Nom</th>
                <th style={styles.th}>Prénom</th>
                <th style={styles.th}>Statut</th>
                {usesCategoryTabs && activeTab === "junior" && (
                  <th style={styles.th}>Catégorie</th>
                )}
                {isPR1 ? (
                  <>
                    <th style={styles.th}>Quotité service (%)</th>
                    <th style={styles.th}>Quotité Port-Royal (%)</th>
                  </>
                ) : (
                  <th style={styles.th}>Quotité (%)</th>
                )}
                {showOrigineColumn && <th style={styles.th}>Origine</th>}
              </tr>
            </thead>
            <tbody>
              {displayedMedecins.map((m, idx) => {
                const isSelected = isInList(m, medecinsGardes);
                // Utiliser medecin_id si disponible, sinon nom+prenom+idx
                const uniqueKey = m.medecin_id || `${m.nom}-${m.prenom}-${idx}`;
                return (
                  <tr key={uniqueKey}>
                    <td style={styles.td}>
                      <NativeToggleCheckbox
                        checked={isSelected}
                        onToggle={() => toggleMedecin(m)}
                        ariaLabel={`${isSelected ? "Retirer" : "Ajouter"} ${m.prenom} ${m.nom}`}
                        style={styles.checkbox}
                      />
                    </td>
                    <td style={styles.td}>{m.nom}</td>
                    <td style={styles.td}>{m.prenom}</td>
                    <td style={styles.td}>
                      {m.statut}
                      {showStatusBadge &&
                        (isSelected ? (
                          <span style={{ ...styles.badge, ...styles.badgeIn }}>{selectedBadgeLabel}</span>
                        ) : (
                          <span style={{ ...styles.badge, ...styles.badgeOut }}>{unselectedBadgeLabel}</span>
                        ))}
                    </td>
                    {usesCategoryTabs && activeTab === "junior" && (
                      <td style={styles.td}>
                        <span style={styles.categoryBadge}>
                          {categorieJuniorLabel(m.categorie_junior)}
                        </span>
                      </td>
                    )}
                    {isPR1 ? (
                      <>
                        <td style={styles.td}>{m.quotite}</td>
                        <td style={styles.td}>
                          {isSelected ? (
                            <PrQuotiteInput
                              value={(() => {
                                const gardeEntry = medecinsGardes.find((g) => isSameMedecin(g, m));
                                return gardeEntry?.quotite_port_royal ?? m.quotite;
                              })()}
                              onCommit={(v) => {
                                setMedecinsGardes(
                                  medecinsGardes.map((g) =>
                                    isSameMedecin(g, m) ? { ...g, quotite_port_royal: v } : g
                                  )
                                );
                              }}
                              style={{
                                width: "76px",
                                padding: "6px 8px",
                                borderRadius: "6px",
                                border: "1px solid #d1d5db",
                                fontSize: "15px",
                                fontWeight: 600,
                              }}
                            />
                          ) : (
                            <span style={{ color: "#9ca3af", fontSize: "14px" }}>—</span>
                          )}
                        </td>
                      </>
                    ) : (
                      <td style={styles.td}>{m.quotite}</td>
                    )}
                    {showOrigineColumn && (
                      <td style={styles.td}>
                        <select
                          value={(() => {
                            const gardeEntry = medecinsGardes.find(
                              (g) => isSameMedecin(g, m)
                            );
                            return gardeEntry?.origine || "interieur";
                          })()}
                          onChange={(e) => changeOrigine(m, e.target.value)}
                          style={{
                            padding: "6px 10px",
                            borderRadius: "6px",
                            border: isSelected ? "1px solid #d1d5db" : "1px solid #e5e7eb",
                            fontSize: "14px",
                            fontWeight: 600,
                            cursor: "pointer",
                            opacity: isSelected ? 1 : 0.72,
                            background: (() => {
                              const gardeEntry = medecinsGardes.find(
                                (g) => isSameMedecin(g, m)
                              );
                              return (gardeEntry?.origine || "interieur") === "interieur" ? "#d1fae5" : "#dbeafe";
                            })(),
                            color: (() => {
                              const gardeEntry = medecinsGardes.find(
                                (g) => isSameMedecin(g, m)
                              );
                              return (gardeEntry?.origine || "interieur") === "interieur" ? "#065f46" : "#1e40af";
                            })(),
                          }}
                        >
                          <option value="interieur">Intérieur</option>
                          <option value="exterieur">Extérieur</option>
                        </select>
                      </td>
                    )}
                  </tr>
                );
              })}
              {displayedMedecins.length === 0 && (
                <tr>
                  <td
                    style={styles.td}
                    colSpan={
                      (isPR1 ? (showOrigineColumn ? 7 : 6) : showOrigineColumn ? 6 : 5) +
                      (usesCategoryTabs && activeTab === "junior" ? 1 : 0)
                    }
                  >
                    Aucun médecin dans cet onglet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div>
            <button
              style={{ ...styles.button, ...styles.btnPrimary }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "💾 Sauvegarde…" : isPR1 ? "💾 Enregistrer la liste du secteur Port-Royal" : `💾 Enregistrer la liste ${libelleSecteur}`}
            </button>
            <button
              style={{ ...styles.button, ...styles.btnSecondary }}
              onClick={handleResetFromMaître}
            >
              🔄 Réinitialiser depuis la liste maître
            </button>
            <button
              style={{ ...styles.button, ...styles.btnSecondary }}
              onClick={handleBack}
            >
              {canUseSimpleBack
                ? "⬅️ Retour à la page précédente"
                : isOrtho
                ? "⬅️ Retour Gestion des Astreintes Orthopédie"
                : isOrthoJunior
                ? "⬅️ Retour Gestion Astreinte junior Orthopédie"
                : isPR1
                ? "⬅️ Retour Gestion des Gardes PR1"
                : isInterne1
                ? "⬅️ Retour Gestion des Gardes Interne PR1"
                : isInterne2
                ? "⬅️ Retour Gestion des Gardes Interne PR2"
                : isDigestif
                ? "⬅️ Retour Gestion des Gardes Senior Digestif"
                : isDouleur
                ? "⬅️ Retour Gestion des Gardes Douleur"
                : isReanimation
                ? "⬅️ Retour Gestion des Gardes Réanimation"
                : "⬅️ Retour Gestion des Gardes PR2"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
